import { useCallback, useRef, useState } from "react"

import { useGridNav } from "../GridNav"
import {
  advanceCell,
  focusAndScroll,
  handleCellKeyNav,
} from "./cellKeyboardNav"

/**
 * Shared keyboard/focus state machine for a navigable grid cell.
 *
 * Replaces the focus / edit / blur / keydown logic that was duplicated across
 * every cell. Cells differ only by a few options:
 *
 * - `mode: "edit"` (default) — text-style cells: first interaction focuses
 *   (ring), Enter or a second click enters edit mode, Escape exits.
 * - `mode: "popover"` — select-style cells (e.g. type): a single click toggles,
 *   Enter opens; `isEditing` tracks the open state.
 * - `mode: "activate"` — non-editing cells (row number, id): Enter/Space calls
 *   `onActivate`; the caller keeps its own `onClick`.
 *
 * All modes participate in roving-tabindex navigation (arrows, Home/End,
 * PageUp/Down) and report focus to the grid so re-entry restores position.
 * Edit-mode cells also support commit-and-advance: Tab / Shift+Tab move to the
 * adjacent cell and Cmd/Ctrl+Enter moves down, opening the next cell in edit
 * mode for rapid data entry.
 *
 * The focus ring itself is drawn in CSS via `td[data-grid-cell]:focus-within`
 * (see TestCaseTable), so it appears for every focused state — keyboard roving,
 * click, and while editing — without depending on React state timing.
 */
/**
 * How a grid cell behaves on interaction: `"edit"` (text-style cells),
 * `"popover"` (select-style cells), or `"activate"` (non-editing cells).
 */
type GridCellMode = "edit" | "popover" | "activate"

interface UseGridCellOptions {
  ariaLabel?: string
  /** ARIA role for the cell. Defaults to "gridcell". */
  role?: string
  mode?: GridCellMode
  disabled?: boolean
  /**
   * Keep edit mode open while focus moves into a portaled popover/dialog/listbox
   * rendered outside the cell (metadata/KB editors). Edit mode only.
   */
  portalAware?: boolean
  /** Non-editing activation handler (activate mode), e.g. toggle row selection. */
  onActivate?: (e: React.SyntheticEvent) => void
}

interface GridCellProps {
  ref: React.RefObject<HTMLTableCellElement | null>
  tabIndex: number
  role: string
  "aria-label"?: string
  "data-grid-cell": string
  onFocus: (e: React.FocusEvent<HTMLTableCellElement>) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLTableCellElement>) => void
  onMouseDown?: () => void
  onClick?: (e: React.MouseEvent) => void
  onBlur?: (e: React.FocusEvent<HTMLTableCellElement>) => void
}

interface UseGridCellResult {
  tdRef: React.RefObject<HTMLTableCellElement | null>
  isFocused: boolean
  isEditing: boolean
  setIsEditing: (editing: boolean) => void
  setIsFocused: (focused: boolean) => void
  /** Props to spread onto the cell's `<td>`. */
  cellProps: GridCellProps
}

function movedToPortal(related: HTMLElement | null): boolean {
  return Boolean(
    related?.closest("[data-slot='content']") ||
    related?.closest("[role='dialog']") ||
    related?.closest("[role='listbox']"),
  )
}

function activeElementInPortal(): boolean {
  const active = document.activeElement
  return Boolean(
    active?.closest("[data-slot='content']") ||
    active?.closest("[role='dialog']") ||
    active?.closest("[role='listbox']"),
  )
}

export function useGridCell({
  ariaLabel,
  role = "gridcell",
  mode = "edit",
  disabled = false,
  portalAware = false,
  onActivate,
}: UseGridCellOptions = {}): UseGridCellResult {
  const gridNav = useGridNav()
  const [isFocused, setIsFocused] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const tdRef = useRef<HTMLTableCellElement>(null)
  const mouseDownRef = useRef(false)

  const handleMouseDown = useCallback(() => {
    mouseDownRef.current = true
  }, [])

  const handleFocus = useCallback(() => {
    gridNav.noteFocusedCell(tdRef.current)
    // Keyboard handoff: a prior cell asked the next one to open in edit mode.
    if (
      !disabled &&
      (mode === "edit" || mode === "popover") &&
      gridNav.consumeAutoEdit()
    ) {
      mouseDownRef.current = false
      setIsFocused(true)
      setIsEditing(true)
      return
    }
    if (!mouseDownRef.current) {
      setIsFocused(true)
    }
    mouseDownRef.current = false
  }, [gridNav, disabled, mode])

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      if (disabled) {
        setIsFocused(true)
        tdRef.current?.focus()
        return
      }
      if (mode === "popover") {
        setIsFocused(true)
        tdRef.current?.focus()
        setIsEditing(!isEditing)
        return
      }
      // edit mode: focus first, edit on the second click.
      if (isFocused) {
        setIsEditing(true)
      } else {
        setIsFocused(true)
        tdRef.current?.focus()
      }
    },
    [disabled, mode, isFocused, isEditing],
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTableCellElement>) => {
      const related = e.relatedTarget as HTMLElement | null
      if (e.currentTarget.contains(related)) {
        return
      }
      // Popover/activate cells only own the focus ring; their open state is
      // managed elsewhere (the Popover, or selection state).
      if (mode !== "edit") {
        setIsFocused(false)
        return
      }
      // Don't collapse while a native file dialog is open.
      if (e.currentTarget.hasAttribute("data-file-dialog-open")) {
        return
      }
      if (portalAware) {
        if (movedToPortal(related)) {
          return
        }
        const cell = e.currentTarget
        setTimeout(() => {
          if (
            !cell.contains(document.activeElement) &&
            !activeElementInPortal()
          ) {
            setIsFocused(false)
            setIsEditing(false)
          }
        }, 0)
        return
      }
      setIsFocused(false)
      setIsEditing(false)
    },
    [mode, portalAware],
  )

  const commitAndAdvance = useCallback(
    (direction: "next" | "prev" | "down") => {
      const td = tdRef.current
      if (!td) {
        return
      }
      setIsEditing(false)
      gridNav.requestAutoEdit()
      const target = advanceCell(td, direction)
      if (target) {
        focusAndScroll(target)
      } else {
        // Nothing to advance to — clear the pending request and stay put.
        gridNav.consumeAutoEdit()
        td.focus()
      }
    },
    [gridNav],
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTableCellElement>) => {
      if (mode === "activate") {
        if ((e.key === "Enter" || e.key === " ") && onActivate) {
          e.preventDefault()
          onActivate(e)
          return
        }
        if (tdRef.current) {
          handleCellKeyNav(e, tdRef.current)
        }
        return
      }

      if (e.key === "Escape" && isEditing) {
        e.preventDefault()
        setIsEditing(false)
        tdRef.current?.focus()
        return
      }
      if (e.key === "Enter" && !isEditing && isFocused) {
        e.preventDefault()
        if (!disabled) {
          setIsEditing(true)
        }
        return
      }
      if (isEditing) {
        // Commit-and-advance shortcuts for rapid entry.
        if (e.key === "Tab") {
          e.preventDefault()
          commitAndAdvance(e.shiftKey ? "prev" : "next")
          return
        }
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          commitAndAdvance("down")
          return
        }
        return
      }
      if (tdRef.current) {
        handleCellKeyNav(e, tdRef.current)
      }
    },
    [mode, isEditing, isFocused, disabled, onActivate, commitAndAdvance],
  )

  const cellProps: GridCellProps = {
    ref: tdRef,
    tabIndex: -1,
    role,
    "aria-label": ariaLabel,
    "data-grid-cell": "",
    onFocus: handleFocus,
    onKeyDown: handleKeyDown,
  }
  if (mode !== "activate") {
    cellProps.onMouseDown = handleMouseDown
    cellProps.onClick = handleClick
  }
  cellProps.onBlur = handleBlur

  return {
    tdRef,
    isFocused,
    isEditing,
    setIsEditing,
    setIsFocused,
    cellProps,
  }
}
